import { useCallback, useEffect, useState } from 'react';

import type {
  GeofenceFilter,
  PlainGeofence,
} from '@cobliteam/common-types/src/geofenceV3';
import type { UUID } from '@cobliteam/common-types/src/identifier';

import { useInfinityGeofences } from '@cobliteam/hooks/src/useEntity/useGeofenceV3';

import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';
import DropdownGeofencesPlaceholder from '../../../components/Dropdown/components/DropdownBodyPlaceholder/DropdownBodyPlaceholder';
import GeofenceItem from './components/GeofenceItem';
import GeofenceSearch from './components/GeofenceSearch';
import GeofenceTitle from './components/GeofenceTitle';

interface MultiSelectGeofencesProps {
  size?: 'condensed' | 'regular';
  selectedGeofencesIds?: UUID[];
  title?: string;
  onChange: (geofences: PlainGeofence[]) => void;
  onOpen?: () => void;
}

const MultiSelectGeofences = ({
  onChange,
  selectedGeofencesIds = [],
  onOpen,
  title,
  size = 'regular',
}: MultiSelectGeofencesProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [isGeofencesFetchEnabled, setIsGeofencesFetchEnabled] = useState(false);
  const [geofencesIdsDraft, setGeofencesIdsDraft] =
    useState<UUID[]>(selectedGeofencesIds);
  const [filters, setFilters] = useState<GeofenceFilter>({
    sort: [{ column: 'createdAt', order: 'desc' }],
  });

  const { data, isLoading, hasNextPage, isFetching, fetchNextPage } =
    useInfinityGeofences({
      filters,
      options: {
        refetchOnMount: false,
        enabled: isGeofencesFetchEnabled,
      },
    });

  const geofencesFlatted = data?.pages.flatMap((page) => page.content) || [];

  // compare 2 arrays has same elements geofences type Set and geofencesDraft type Set
  const hasSameElements =
    selectedGeofencesIds?.length === geofencesIdsDraft?.length &&
    selectedGeofencesIds.every((value) => geofencesIdsDraft?.includes(value));

  const openDropdown = useCallback(() => {
    setIsGeofencesFetchEnabled(true);
    setGeofencesIdsDraft(selectedGeofencesIds);
    onOpen?.();
  }, [onOpen, selectedGeofencesIds]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setGeofencesIdsDraft(selectedGeofencesIds);
    setFilters({ sort: [{ column: 'createdAt', order: 'desc' }] });
  }, [selectedGeofencesIds]);

  const handleApply = () => {
    const selectedGeofences = geofencesFlatted.filter((geofence) =>
      geofencesIdsDraft.includes(geofence.id)
    );
    onChange(selectedGeofences);
    handleClose();
  };

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (dataItem: PlainGeofence) => {
      const newItemDraft = Array.from(geofencesIdsDraft);
      const existItem = newItemDraft.includes(dataItem.id);
      if (existItem) newItemDraft.splice(newItemDraft.indexOf(dataItem.id), 1);
      if (!existItem) newItemDraft.push(dataItem.id);
      setGeofencesIdsDraft(newItemDraft);
    },
    [geofencesIdsDraft, setGeofencesIdsDraft]
  );

  return (
    <Dropdown
      onOpenChange={openDropdown}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-5'>
            <Button
              variant='text'
              size='condensed'
              disabled={geofencesIdsDraft?.length === 0}
              onClick={() => setGeofencesIdsDraft([])}
            >
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Search className='px-5'>
            <GeofenceSearch filters={filters} setFilters={setFilters} />
          </Dropdown.Search>
          <Dropdown.Body>
            <Dropdown.ListPageable
              heightList={300}
              heightItem={40}
              isLoading={isLoading}
              isFetching={isFetching}
              hasNextPage={hasNextPage}
              data={geofencesFlatted}
              placeholderComponent={<DropdownGeofencesPlaceholder />}
              loadingComponent={<Dropdown.Loading />}
              fetchNextPage={fetchNextPage}
              itemComponent={(item) => (
                <GeofenceItem
                  geofence={item}
                  onChange={() => toggleItem(item)}
                  checked={Boolean(geofencesIdsDraft.includes(item.id))}
                />
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button
                variant='text'
                full
                size='condensed'
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                size='condensed'
                full
                onClick={handleApply}
                disabled={hasSameElements}
              >
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <GeofenceTitle
        size={size}
        selectedGeofencesIds={selectedGeofencesIds}
        title={title}
      />
    </Dropdown>
  );
};

export default MultiSelectGeofences;
